import { useRef, useState } from "preact/hooks";
import type { ComponentChildren, VNode } from "preact";
import { ConfirmDialog, type ConfirmTone } from "../components/ui/confirm-dialog";

export type ConfirmOptions = {
  title: string;
  description?: ComponentChildren;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
};

// Promise-based confirm. Returns a JSX element to mount once per consumer and
// a `confirm(opts)` that resolves to true/false based on the user's choice.
// Cancel paths (Esc, backdrop click, cancel button) all resolve to false.
export function useConfirm() {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  function confirm(o: ConfirmOptions): Promise<boolean> {
    // If something is already open, resolve the previous one as canceled —
    // shouldn't happen with disciplined await usage but keeps state clean.
    resolverRef.current?.(false);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setOpts(o);
    });
  }

  function resolve(value: boolean) {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOpts(null);
  }

  const dialog: VNode | null = opts ? (
    <ConfirmDialog
      open
      title={opts.title}
      description={opts.description}
      confirmLabel={opts.confirmLabel}
      cancelLabel={opts.cancelLabel}
      tone={opts.tone}
      onClose={() => resolve(false)}
      onConfirm={() => resolve(true)}
    />
  ) : null;

  return { confirm, dialog };
}
